// Ticket 142: small formatting helpers shared by the Inbox list row and
// thread pane. No PII in comments/log lines — dates, ids and channel keys only.
import { MessageSquare, Mail, Phone, Voicemail } from 'lucide-react';
import { fromApi } from '../kit/time';

export const CHANNEL_META = {
  sms: { icon: MessageSquare, label: 'SMS' },
  email: { icon: Mail, label: 'Email' },
  call: { icon: Phone, label: 'Call' },
  voicemail: { icon: Voicemail, label: 'Voicemail' },
};

export function channelIcon(channel) {
  return (CHANNEL_META[channel] || CHANNEL_META.sms).icon;
}

export function channelLabel(channel) {
  return (CHANNEL_META[channel] || {}).label || channel;
}

// Row date (decisions doc): time if today, "Sep 23" this year, "Sep 23, 2025"
// older. `iso` is a naive-UTC API datetime (or null for a contact with no
// messages yet).
export function formatRowDate(iso) {
  if (!iso) return '';
  const d = fromApi(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString('en-US', sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

// A call/voicemail thread with no text body still needs a one-line preview.
export function rowPreview(item) {
  if (item.last_preview) return item.last_preview;
  if (item.last_channel === 'call') return 'Call';
  if (item.last_channel === 'voicemail') return 'Voicemail';
  return '—';
}

// Ticket 164: thread day separators ("Today", "Yesterday", "Sep 28", "Sep 28,
// 2025") and the short bubble time ("5:12 PM" — no seconds). Local zone.
export function dayLabel(iso) {
  const d = fromApi(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString('en-US', sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatBubbleTime(iso) {
  const d = fromApi(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// Split an ordered message list into day groups for the separators. Messages
// without a parseable date join the group before them (or an unlabeled first).
export function groupByDay(messages) {
  const groups = [];
  messages.forEach((m) => {
    const d = fromApi(m.created_at);
    const key = Number.isNaN(d.getTime()) ? null : d.toDateString();
    const last = groups[groups.length - 1];
    if (last && (key === null || last.key === key)) { last.items.push(m); return; }
    groups.push({ key, label: key ? dayLabel(m.created_at) : '', items: [m] });
  });
  return groups;
}

// Ticket 195: the Message details modal. Full date + time with the zone name
// ("Oct 5, 2026 at 2:05:00 PM CDT"), in the viewer's local zone.
export function formatFullTimestamp(iso) {
  const d = fromApi(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'long' });
}

const asAddress = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : (typeof v === 'string' ? v.trim() : ''));

// Who the message went from / to. Only the contact's end is always known (their
// phone or email). The business end is recorded for emails (GoHighLevel's
// from/to), for a send BFY made (`to`) and wherever the row stored `from`/`to`
// (legacy Twilio texts); a GHL text or call carries no business number, so that
// side is null — the modal says "Not recorded", never a guess.
// `contact` = { name, phone, email }; each side = { label, address } or null.
export function messageParties(m, contact = {}) {
  const email = m.channel === 'email';
  const contactAddress = email ? contact.email : contact.phone;
  const contactSide = (address) => {
    const a = address || contactAddress || '';
    return contact.name || a ? { label: contact.name || '', address: a } : null;
  };
  const ghlEmail = m.ghl?.email || {};
  const businessSide = (address) => (address ? { label: '', address } : null);
  if (m.direction === 'outbound') {
    return {
      from: businessSide((email && asAddress(ghlEmail.from)) || asAddress(m.from)),
      to: contactSide(asAddress(m.to) || (email ? asAddress(ghlEmail.to) : '')),
    };
  }
  return {
    from: contactSide((email && asAddress(ghlEmail.from)) || asAddress(m.from)),
    to: businessSide((email && asAddress(ghlEmail.to)) || asAddress(m.to)),
  };
}
